import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useCheckPermission } from "../../../../hooks/useCheckPermission";
import {
  setEnabledQueryDailyForOverlimit,
  setReportsDailyForOverlimit,
  setTotalCountDailyForOverlimit,
  useSelectEnabledDailyForOverlimit,
  useSelectFiltersDailyForOverlimit,
  useSelectPageDailyForOverlimit,
  useSelectSizeDailyForOverlimit,
  useSelectTotalCountDailyForOverlimit,
} from "../../../../redux/features/reports/dailyForOverlimitSlice";
import { generate, getReportLabels } from "../../../../services/report";

const useDailyForOverlimitFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelectFiltersDailyForOverlimit();
  const exportPermission = useCheckPermission("DOWNLOAD_REPORT");

  const page = useSelectPageDailyForOverlimit();
  const size = useSelectSizeDailyForOverlimit();
  const totalCount = useSelectTotalCountDailyForOverlimit();
  const enabled = useSelectEnabledDailyForOverlimit();

  const setEnabled = (val) => dispatch(setEnabledQueryDailyForOverlimit(val));

  const {
    isLoading,
    isFetching,
    refetch,
    error,
    data: queryData,
  } = useQuery({
    queryKey: ["get-daily-for-overlimit-report", page, size, filters],
    queryFn: () => generate(page, size, filters),
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: enabled,
  });

  useEffect(() => {
    if (!queryData) return;
    dispatch(setTotalCountDailyForOverlimit(queryData.elements ?? 0));
    dispatch(setReportsDailyForOverlimit(queryData.data ?? []));
  }, [queryData, dispatch]);

  useEffect(() => {
    if (!error || isFetching) return;
    toast.error(
      error?.response?.data?.message || "Error fetching daily overlimit report",
      { toastId: "daily-for-overlimit-fetch-error" },
    );

    dispatch(setReportsDailyForOverlimit([]));
    dispatch(setTotalCountDailyForOverlimit(0));
  }, [error, isFetching, dispatch]);

  return {
    page,
    size,
    totalCount,
    exportPermission,
    isLoading,
    enabled,
    getReports: refetch,
    setEnabled,
  };
};

export const useReportLabels = () => {
  return useQuery({
    queryKey: ["get-report-labels"],
    queryFn: async () => {
      const resp = await getReportLabels();
      return resp?.data ?? {};
    },
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
};

export default useDailyForOverlimitFilters;
